import { computed, reactive, ref } from 'vue'
import { defineStore } from 'pinia'
import {
  ApiError,
  csv,
  getOperation,
  listAssetOperations,
  listOperations,
  members,
  REAUTH_HEADER,
  requestOperation,
  retryOperation,
  toOperationView,
  useApiClient,
  type OperationView,
} from '@/api'
import { useAuthStore } from './auth'
import { describeApiError } from '@/i18n/errors'

/**
 * Operations, and the polling loop that watches them.
 *
 * The states are the protocol's, not ours (`capabilities.md` §3.2), and each means something
 * different to the person watching:
 *
 * | State     | What happened                    | What the interface does                       |
 * |-----------|----------------------------------|-----------------------------------------------|
 * | `queued`  | accepted, not yet dispatched     | show it, so a slow queue is visible not silent |
 * | `running` | more work remains                | progress bar and the agent's own message       |
 * | `done`    | finished                         | terminal; show the result                      |
 * | `error`   | we failed                        | terminal; offer a retry **only** if retryable  |
 * | `blocked` | the site refused                 | terminal; explain, and never retry             |
 *
 * The `blocked` row is the one that matters. `capabilities.md` §3.6: *"Blocked is never retried
 * automatically… Retrying a blocked operation is how monitoring systems learn to cry wolf."* This
 * store therefore has no automatic retry at all — a retry is always a person pressing a button.
 *
 * ## There is no cancel
 *
 * The previous client called `POST /operations/{id}/cancel`. No such endpoint exists and none ever
 * did; the core offers `POST /operations/{id}/retry` and nothing else. An operation that has been
 * dispatched to an agent is being executed on somebody's live site, and "cancel" would have to
 * mean either "stop mid-update" — which is how a site ends up half-updated — or "stop asking",
 * which is not the same thing and must not look like it. The button is gone rather than faked.
 */

/** The five states `capabilities.md` §3.2 defines. Open in principle; these are the known ones. */
export type OperationState = 'queued' | 'running' | 'done' | 'error' | 'blocked'

/**
 * Has this operation stopped?
 *
 * The core answers this itself, on every operation, in `terminal` — see `toOperationView`, which
 * falls back to the state name only for a fixture or an older core.
 */
export const isTerminal = (operation: Pick<OperationView, 'terminal'>): boolean =>
  operation.terminal

/** Backoff for the watch loop: quick while a person is watching, slower once it settles. */
const POLL_SCHEDULE_MS = [1_000, 1_000, 2_000, 2_000, 3_000, 5_000, 8_000, 10_000] as const

export interface DispatchRequest {
  assetId: string
  capability: string
  params?: Record<string, unknown>
  /** Single-use ticket from `POST /auth/reauthenticate`, for a destructive capability (§11.4). */
  reauthTicket?: string | null
}

export const useOperationsStore = defineStore('operations', () => {
  const api = useApiClient()
  const auth = useAuthStore()

  /** Every operation we know about, by id. One source of truth for list and detail views. */
  const byId = reactive<Record<string, OperationView | undefined>>({})

  /** Ids being polled, with their timers, so a component unmount can stop watching. */
  const watching = new Map<string, ReturnType<typeof setTimeout>>()
  const watchTicks = new Map<string, number>()

  const listLoading = ref(false)
  const listError = ref<string | null>(null)
  const dispatching = ref(false)
  const lastError = ref<ApiError | null>(null)

  const all = computed(() =>
    Object.values(byId)
      .filter((op): op is OperationView => op !== undefined)
      .sort((a, b) => (b.created_at ?? '').localeCompare(a.created_at ?? '')),
  )

  const active = computed(() => all.value.filter((op) => !isTerminal(op)))

  const forAsset = (assetId: string): OperationView[] =>
    all.value.filter((op) => op.asset_id === assetId)

  const activeForAsset = (assetId: string): OperationView[] =>
    forAsset(assetId).filter((op) => !isTerminal(op))

  const absorb = (operation: OperationView): OperationView => {
    byId[operation.id] = operation
    return operation
  }

  const fetchList = async (
    query: {
      asset?: string[]
      capability?: string[]
      state?: string[]
      requested_by?: string
      from?: string
      to?: string
      itemsPerPage?: number
    } = {},
  ): Promise<void> => {
    listLoading.value = true
    listError.value = null

    try {
      const result = await api.call(listOperations, {
        query: {
          itemsPerPage: query.itemsPerPage ?? 50,
          asset: csv(query.asset),
          capability: csv(query.capability),
          state: csv(query.state),
          requested_by: query.requested_by,
          from: query.from,
          to: query.to,
        },
      })
      for (const operation of members(result)) absorb(toOperationView(operation))
    } catch (error) {
      listError.value = describeApiError(error, 'errors.loadOperations')
    } finally {
      listLoading.value = false
    }
  }

  const fetchForAsset = async (assetId: string, itemsPerPage = 25): Promise<void> => {
    try {
      const result = await api.call(listAssetOperations, {
        path: { assetId },
        query: { itemsPerPage },
      })
      for (const operation of members(result)) absorb(toOperationView(operation))
    } catch (error) {
      listError.value = describeApiError(error, 'errors.loadOperations')
    }
  }

  const fetchOne = async (operationId: string): Promise<OperationView | null> => {
    try {
      return absorb(toOperationView(await api.call(getOperation, { path: { id: operationId } })))
    } catch (error) {
      if (error instanceof ApiError) lastError.value = error
      return null
    }
  }

  /**
   * Request an operation, obtaining a fresh factor first when the capability needs one.
   *
   * The re-auth prompt happens *here*, in the same call stack as the dispatch, rather than as a
   * separate step a caller might forget. Design §11.4 requires the fresh factor and the audit
   * record before dispatch; the core enforces both, and this makes the interface agree with it
   * instead of discovering the requirement through a 403.
   *
   * The ticket travels in the `X-Reauthentication` header — the name the core reads — rather than
   * in the body, so it does not end up in anything that logs or replays request bodies.
   */
  const dispatch = async (request: DispatchRequest): Promise<OperationView | null> => {
    dispatching.value = true
    lastError.value = null

    try {
      const operation = toOperationView(await api.call(requestOperation, {
        path: { assetId: request.assetId },
        body: {
          capability: request.capability,
          // Cast for the same reason as `paramsOf`: the specification types this as an array of
          // strings, the core reads it as a free-form object, and the object is what it wants.
          params: (request.params ?? {}) as never,
        },
        headers: request.reauthTicket
          ? ({ [REAUTH_HEADER]: request.reauthTicket } as never)
          : undefined,
      }))

      absorb(operation)
      watch(operation.id)
      return operation
    } catch (error) {
      if (error instanceof ApiError) {
        lastError.value = error

        // The core may decide a capability needs a fresh factor that the client's registry did
        // not mark. The core is authoritative; prompt and try once more rather than telling
        // somebody their permitted action is forbidden.
        if (error.needsReauth && !request.reauthTicket) {
          const ticket = await auth.requestReauth({
            capability: request.capability,
            assetId: request.assetId,
            label: request.capability,
          })
          if (ticket) return dispatch({ ...request, reauthTicket: ticket })
        }
      }
      return null
    } finally {
      dispatching.value = false
    }
  }

  /**
   * Retry a finished operation as a **new** operation.
   *
   * `POST /operations/{id}/retry` allocates a fresh id server-side. It is never a re-submission of
   * the same one: `op_id` is an idempotency key, and re-submitting it returns the stored terminal
   * state rather than doing the work again (`capabilities.md` §3.3). That property is what stops a
   * double install.
   *
   * Refuses on `blocked`, because the block is a condition on the site that has to change first,
   * and on anything the core marked `retryable: false`.
   */
  const retry = async (operationId: string): Promise<OperationView | null> => {
    const previous = byId[operationId]
    if (!previous) return null
    if (previous.state === 'blocked') return null
    if (previous.retryable === false) return null

    try {
      const operation = toOperationView(
        await api.call(retryOperation, { path: { id: operationId } }),
      )
      absorb(operation)
      watch(operation.id)
      return operation
    } catch (error) {
      if (error instanceof ApiError) {
        lastError.value = error

        if (error.needsReauth) {
          const ticket = await auth.requestReauth({
            capability: previous.capability,
            assetId: previous.asset_id,
            label: previous.capability,
          })

          if (ticket) {
            try {
              const operation = toOperationView(
                await api.call(retryOperation, {
                  path: { id: operationId },
                  headers: { [REAUTH_HEADER]: ticket } as never,
                }),
              )
              absorb(operation)
              watch(operation.id)
              return operation
            } catch (second) {
              if (second instanceof ApiError) lastError.value = second
            }
          }
        }
      }
      return null
    }
  }

  // --- polling ------------------------------------------------------------

  /**
   * Poll one operation until it reaches a terminal state.
   *
   * Polling, not streaming, because the core drives the step loop on its own schedule and there
   * is nothing to push at the moment a step lands. The interval widens as the operation ages: a
   * `core.update` on a large site takes minutes, and a one-second poll for all of them is a
   * self-inflicted load problem.
   */
  const watch = (operationId: string): void => {
    if (watching.has(operationId)) return
    watchTicks.set(operationId, 0)

    const tick = async (): Promise<void> => {
      const operation = await fetchOne(operationId)

      if (!operation || isTerminal(operation)) {
        unwatch(operationId)
        return
      }

      const index = watchTicks.get(operationId) ?? 0
      watchTicks.set(operationId, index + 1)
      const delay = POLL_SCHEDULE_MS[Math.min(index, POLL_SCHEDULE_MS.length - 1)]
      watching.set(operationId, setTimeout(tick, delay))
    }

    watching.set(operationId, setTimeout(tick, POLL_SCHEDULE_MS[0]))
  }

  const unwatch = (operationId: string): void => {
    const timer = watching.get(operationId)
    if (timer) clearTimeout(timer)
    watching.delete(operationId)
    watchTicks.delete(operationId)
  }

  /** Stop every poll. Called when a view unmounts, so a background tab does not keep polling. */
  const unwatchAll = (): void => {
    for (const id of [...watching.keys()]) unwatch(id)
  }

  const isWatching = (operationId: string): boolean => watching.has(operationId)

  /** Adopt whatever is already in flight for an asset, and watch it. */
  const syncAsset = async (assetId: string): Promise<void> => {
    await fetchForAsset(assetId)
    for (const operation of activeForAsset(assetId)) watch(operation.id)
  }

  return {
    byId,
    all,
    active,
    listLoading,
    listError,
    dispatching,
    lastError,
    forAsset,
    activeForAsset,
    fetchList,
    fetchForAsset,
    fetchOne,
    dispatch,
    retry,
    watch,
    unwatch,
    unwatchAll,
    isWatching,
    syncAsset,
    absorb,
  }
})
